import axios from "axios";
import { DateTime } from "luxon";
import { toast } from "react-toastify";

// Fetch app details by App ID
export const fetchAppDetailsByAppId = async (appId) => {
  if (!appId || appId.length < 2) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/appDetailsByAppId?app_id=${appId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching app details by app ID:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch app details", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Fetch app details by App Name
export const fetchAppDetailsByAppName = async (appName) => {
  if (!appName || appName.length < 2) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/appDetailsByAppName?app_name=${encodeURIComponent(appName)}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching app details by app name:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch app details by name", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Fetch incident details
export const fetchIncidentDetails = async (incidentId) => {
  if (!incidentId || incidentId.length < 3) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/incidentDetails?incident_id=${incidentId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    if (response.data?.status === "success" && response.data?.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("Error fetching incident details:", error);
    if (error.response?.status !== 404) {
      toast.error("Failed to fetch incident details", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    return null;
  }
};

// Submit new application
export const submitNewApplication = async (formData, userEmail) => {
  const payload = {
    app_id: formData.app_id,
    app_name: formData.app_name,
    platform: formData.platform,
    portfolio: formData.portfolio,
    status: formData.status,
    trigger_type: formData.trigger_type,
    incident: formData.incident,
    priority: formData.priority,
    comments: formData.comments,
    added_to_red_channel: DateTime.isDateTime(formData.added_to_red_channel)
      ? formData.added_to_red_channel.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.added_to_red_channel).toFormat("yyyy-MM-dd HH:mm:ss"),
    incident_dates: formData.incident_dates,
    final_review: DateTime.isDateTime(formData.final_review)
      ? formData.final_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.final_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    deployment_type: formData.deployment_type,
    deployment_details: formData.deployment_details,
  };

  if (formData.status === "Green" && formData.moved_to_green_channel_on) {
    payload.moved_to_green_channel_on = DateTime.isDateTime(formData.moved_to_green_channel_on)
      ? formData.moved_to_green_channel_on.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.moved_to_green_channel_on).toFormat("yyyy-MM-dd HH:mm:ss");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?user_email=${userEmail}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update existing application
export const updateApplication = async (formData, userEmail, originalAppName) => {
  const payload = {
    app_id: formData.app_id,
    app_name: formData.app_name,
    platform: formData.platform,
    portfolio: formData.portfolio,
    status: formData.status,
    trigger_type: formData.trigger_type,
    comments: formData.comments,
    added_to_red_channel: DateTime.isDateTime(formData.added_to_red_channel)
      ? formData.added_to_red_channel.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.added_to_red_channel).toFormat("yyyy-MM-dd HH:mm:ss"),
    final_review: DateTime.isDateTime(formData.final_review)
      ? formData.final_review.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.final_review).toFormat("yyyy-MM-dd HH:mm:ss"),
    deployment_type: formData.deployment_type,
    deployment_details: formData.deployment_details,
  };

  if (formData.status === "Green" && formData.moved_to_green_channel_on) {
    payload.moved_to_green_channel_on = DateTime.isDateTime(formData.moved_to_green_channel_on)
      ? formData.moved_to_green_channel_on.toFormat("yyyy-MM-dd HH:mm:ss")
      : DateTime.fromJSDate(formData.moved_to_green_channel_on).toFormat("yyyy-MM-dd HH:mm:ss");
  }

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel?user_email=${userEmail}&app_name=${encodeURIComponent(originalAppName)}`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Validate mandatory fields
export const validateMandatoryFields = (formData, isEditMode = false) => {
  const mandatoryFields = {
    app_name: "App Instance Name",
    platform: "Platform",
    portfolio: "Portfolio",
    status: "Status",
    trigger_type: "Trigger",
    added_to_red_channel: "Added to Red Channel",
    final_review: "Final Review Date",
    deployment_type: "Deployment Type",
  };

  // Only require incident and priority in add mode, not in edit mode
  if (!isEditMode) {
    mandatoryFields.incident = "Incident ID";
    mandatoryFields.priority = "Priority";
  }

  const missingFields = [];
  for (const [key, label] of Object.entries(mandatoryFields)) {
    if (!formData[key]) {
      missingFields.push(label);
    }
  }

  // Check if deployment_details is required when deployment_type is "automated"
  if (formData.deployment_type && formData.deployment_type.toLowerCase() === "automated") {
    if (!formData.deployment_details || formData.deployment_details.trim() === "") {
      missingFields.push("Deployment Details (required for automated deployment)");
    }
  }

  return missingFields;
};

// Fetch statistics data
export const fetchStatisticsData = async (appId, appName, addedToRedChannelDate, incidentId) => {
  try {
    // Handle blank or missing app_id by sending null
    const sanitizedAppId = appId && appId.trim() !== "" ? appId : null;
    
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/applicationStatistics`,
      {
        params: { app_id: sanitizedAppId, app_name: appName, added_to_red_channel: addedToRedChannelDate, incident_id: incidentId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
        },
      }
    );

    // Transform response to match expected structure
    const data = response.data;
    return {
      statisticsData: data.application_changes || null,
      incidentCounts: data.incident_details || null,
      postmortemPreventiveMeasures: data.postmortem_preventive_measures || [],
      oldestIncidentDate: data.oldest_incident_date || null,
    };
  } catch (error) {
    console.error("Error fetching statistics:", error);
    toast.error("Failed to fetch statistics data");
    throw error;
  }
};
